import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Crown, Search, Users } from "lucide-react";
import { Modal } from "@/shared/components/Modal";
import { Button } from "@/shared/components/Button";
import { FormularioMinisterio } from "@/core/components/FormularioMinisterio";
import { useCriarMinisterio, useMinisterios } from "@/core/hooks/useMinisterios";

/**
 * Ministérios (Núcleo, só admin da plataforma — DA-002 Nível 1 / DA-004):
 * lista os ministérios da igreja com líderes e total de integrantes. Criar
 * abre modal; gerenciar (dados, funções, integrantes, líder) é na página de
 * detalhe (/admin/ministerios/:id).
 */
export function GerenciarMinisterios() {
  const { data: ministerios, isLoading, isError } = useMinisterios();
  const criar = useCriarMinisterio();
  const navigate = useNavigate();

  const [modalCriar, setModalCriar] = useState(false);
  const [erroFormulario, setErroFormulario] = useState<string | null>(null);
  const [pesquisa, setPesquisa] = useState("");
  const [mensagem, setMensagem] = useState<string | null>(null);
  // F2-05 (revisão 20/07/2026): faltava filtrar por status (ativo/inativo).
  const [filtroStatus, setFiltroStatus] = useState<"todos" | "ativo" | "inativo">("todos");

  const listaFiltrada = useMemo(() => {
    if (!ministerios) return ministerios;
    const termo = pesquisa.trim().toLowerCase();
    return ministerios.filter((m) => {
      if (filtroStatus === "ativo" && !m.ativo) return false;
      if (filtroStatus === "inativo" && m.ativo) return false;
      if (!termo) return true;
      return m.nome.toLowerCase().includes(termo);
    });
  }, [ministerios, pesquisa, filtroStatus]);

  async function handleCriar(dados: { nome: string; descricao: string | null }) {
    setErroFormulario(null);
    try {
      const novo = await criar.mutateAsync(dados);
      setModalCriar(false);
      setMensagem(`Ministério "${novo.nome}" criado, já com a função "Líder".`);
      setTimeout(() => setMensagem(null), 3500);
    } catch (e) {
      setErroFormulario(e instanceof Error ? e.message : "Não foi possível criar o ministério.");
    }
  }

  return (
    <>
      <main className="mx-auto w-full max-w-4xl px-4 pb-12 pt-8 sm:px-6 sm:pt-10">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-black tracking-tight text-primary sm:text-3xl">
              Ministérios
            </h1>
            <p className="mt-1 text-sm text-muted">
              Áreas de atuação da igreja. Abra um ministério para gerenciar informações, funções,
              integrantes e o líder.
            </p>
          </div>
          <Button
            onClick={() => {
              setErroFormulario(null);
              setModalCriar(true);
            }}
            className="shrink-0"
          >
            + Novo ministério
          </Button>
        </div>

        {mensagem && (
          <p role="alert" className="mt-4 rounded-xl bg-success/10 px-4 py-3 text-sm text-success">
            {mensagem}
          </p>
        )}

        {/* F2-05: filtro por status (ativo/inativo). */}
        <div className="mt-4 flex gap-1.5 overflow-x-auto">
          {(
            [
              { valor: "todos", rotulo: "Qualquer status" },
              { valor: "ativo", rotulo: "Ativos" },
              { valor: "inativo", rotulo: "Inativos" },
            ] as { valor: "todos" | "ativo" | "inativo"; rotulo: string }[]
          ).map((op) => (
            <button
              key={op.valor}
              type="button"
              onClick={() => setFiltroStatus(op.valor)}
              className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                filtroStatus === op.valor
                  ? "bg-surface-2 text-text ring-1 ring-primary"
                  : "border border-border bg-surface text-muted hover:text-text"
              }`}
            >
              {op.rotulo}
            </button>
          ))}
        </div>

        <div className="relative mt-4">
          <Search
            size={16}
            className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted"
          />
          <input
            type="search"
            placeholder="Pesquisar ministério..."
            value={pesquisa}
            onChange={(e) => setPesquisa(e.target.value)}
            className="w-full rounded-xl border border-border bg-surface py-3 pl-11 pr-4 text-sm text-text placeholder:text-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        {isLoading ? (
          <p className="mt-4 text-sm text-muted">Carregando...</p>
        ) : isError ? (
          <p role="alert" className="mt-4 rounded-xl bg-danger/10 px-4 py-3 text-sm text-danger">
            Não foi possível carregar os ministérios. Tente novamente.
          </p>
        ) : !listaFiltrada?.length ? (
          <p className="mt-4 rounded-xl border border-border bg-surface p-6 text-sm text-muted">
            {pesquisa
              ? "Nenhum ministério encontrado."
              : "Nenhum ministério cadastrado ainda. Crie o primeiro para organizar as áreas da igreja."}
          </p>
        ) : (
          <div className="mt-4 space-y-3">
            {listaFiltrada.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => navigate(`/admin/ministerios/${m.id}`)}
                className="group flex w-full items-center gap-4 rounded-2xl border border-border bg-surface p-4 text-left transition-all hover:-translate-y-0.5 hover:border-primary hover:shadow-md sm:p-5"
              >
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
                  <Users size={22} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2">
                    {/* F2-06 (revisão 20/07/2026): nome de teste bem grande quebrava o
                        card inteiro — trunca com reticências e mostra completo no title. */}
                    <span className="max-w-[14rem] truncate font-bold text-text" title={m.nome}>
                      {m.nome}
                    </span>
                    {!m.ativo && (
                      <span className="shrink-0 rounded-full bg-surface-2 px-2 py-0.5 text-[10px] font-semibold text-muted">
                        Inativo
                      </span>
                    )}
                  </span>
                  <span className="mt-0.5 flex items-center gap-1 text-xs text-muted">
                    <Crown size={12} className="shrink-0" />
                    <span
                      className="truncate"
                      title={m.lideres.length ? m.lideres.join(", ") : undefined}
                    >
                      {m.lideres.length ? m.lideres.join(", ") : "Sem líder definido"}
                    </span>
                    <span className="shrink-0">
                      {" · "}
                      {m.totalIntegrantes} {m.totalIntegrantes === 1 ? "integrante" : "integrantes"}
                    </span>
                  </span>
                </span>
                <ChevronRight
                  size={18}
                  className="shrink-0 text-muted transition-transform group-hover:translate-x-0.5 group-hover:text-primary"
                />
              </button>
            ))}
          </div>
        )}
      </main>

      <Modal titulo="Novo ministério" aberto={modalCriar} onClose={() => setModalCriar(false)} carregando={criar.isPending}>
        <FormularioMinisterio
          carregando={criar.isPending}
          erro={erroFormulario}
          onSubmit={handleCriar}
          onFechar={() => setModalCriar(false)}
        />
      </Modal>
    </>
  );
}
